import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminShell from '../components/admin/AdminShell';
import {
  fetchActiveAdminCustomerPromotion,
  fetchAdminCustomerPromotionHistory,
  updateActiveAdminCustomerPromotion,
} from '../services/api/admin';
import type { AdminCustomerPromotion, AdminCustomerPromotionPayload } from '../types/admin';
import { formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/admin.css';

const EMPTY_FORM = {
  code: '',
  title: '',
  description: '',
  status: 'ACTIVE',
  discountPercent: '20',
  maxRedemptions: '1',
  localDistanceKm: '25',
  startsAt: '',
  endsAt: '',
  assignOnSignup: true,
};

function toDateTimeLocal(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const offsetMs = parsed.getTimezoneOffset() * 60_000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toInstant(value: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
}

function numberValue(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeLabel(value: string | null | undefined): string {
  return (value || 'UNKNOWN')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusPillClass(status: string | null | undefined): string {
  if (status === 'ACTIVE') return 'admin-pill admin-pill-success';
  if (status === 'DRAFT') return 'admin-pill admin-pill-active';
  if (status === 'PAUSED') return 'admin-pill admin-pill-warning';
  if (status === 'ENDED') return 'admin-pill admin-pill-danger';
  return 'admin-pill';
}

function promotionToForm(promotion: AdminCustomerPromotion) {
  return {
    code: promotion.code || '',
    title: promotion.title || '',
    description: promotion.description || '',
    status: promotion.status || 'ACTIVE',
    discountPercent: String(promotion.discountPercent || 20),
    maxRedemptions: String(promotion.maxRedemptions || 1),
    localDistanceKm: String(promotion.localDistanceKm || 25),
    startsAt: toDateTimeLocal(promotion.startsAt),
    endsAt: toDateTimeLocal(promotion.endsAt),
    assignOnSignup: promotion.assignOnSignup,
  };
}

function defaultStartsAt(): string {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
}

const AdminCustomerPromotionsPage: React.FC = () => {
  const [activePromotion, setActivePromotion] = useState<AdminCustomerPromotion | null>(null);
  const [history, setHistory] = useState<AdminCustomerPromotion[]>([]);
  const [form, setForm] = useState({ ...EMPTY_FORM, startsAt: defaultStartsAt() });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const previewTitle = useMemo(() => {
    const percent = numberValue(form.discountPercent, 0);
    return form.title.trim() || `${percent}% off your first local shift`;
  }, [form.discountPercent, form.title]);

  const loadPromotions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [active, historyPage] = await Promise.all([
        fetchActiveAdminCustomerPromotion(),
        fetchAdminCustomerPromotionHistory({ page: 0, size: 10 }),
      ]);
      setActivePromotion(active);
      setHistory(historyPage.items);
      setForm({
        ...promotionToForm(active),
        startsAt: toDateTimeLocal(active.startsAt) || defaultStartsAt(),
      });
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load customer promotions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPromotions();
  }, [loadPromotions]);

  const updateForm = (field: keyof typeof form, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitPromotion = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    const discountPercent = numberValue(form.discountPercent, 0);
    const maxRedemptions = numberValue(form.maxRedemptions, 0);
    const localDistanceKm = numberValue(form.localDistanceKm, 0);

    if (discountPercent < 1 || discountPercent > 100) {
      setSaving(false);
      setError('Discount percent must be between 1 and 100.');
      setSettingsExpanded(true);
      return;
    }
    if (maxRedemptions < 1) {
      setSaving(false);
      setError('Max redemptions must be at least 1.');
      setSettingsExpanded(true);
      return;
    }
    if (localDistanceKm <= 0) {
      setSaving(false);
      setError('Local distance must be greater than 0 km.');
      setSettingsExpanded(true);
      return;
    }

    const payload: AdminCustomerPromotionPayload = {
      code: form.code.trim() || null,
      title: previewTitle,
      description: form.description.trim() || null,
      status: form.status,
      discountPercent,
      maxRedemptions,
      localDistanceKm,
      startsAt: toInstant(form.startsAt || defaultStartsAt()),
      endsAt: form.endsAt ? toInstant(form.endsAt) : null,
      assignOnSignup: form.assignOnSignup,
    };

    try {
      const saved = await updateActiveAdminCustomerPromotion(payload);
      setActivePromotion(saved);
      setMessage('Signup promotion updated.');
      const historyPage = await fetchAdminCustomerPromotionHistory({ page: 0, size: 10 });
      setHistory(historyPage.items);
      setForm({
        ...promotionToForm(saved),
        startsAt: toDateTimeLocal(saved.startsAt) || defaultStartsAt(),
      });
    } catch (saveError: any) {
      setError(saveError.message || 'Could not save the signup promotion.');
      setSettingsExpanded(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell
      badge="Customer Promotions"
      title="Signup Promotion"
      copy="Set the offer new customers receive when they create an account."
    >
      {error ? <div className="register-alert register-alert-error">{error}</div> : null}
      {message ? <div className="register-alert register-alert-success">{message}</div> : null}

      <section className="admin-grid admin-grid-cards">
        <article className="admin-stat-card">
          <span>Active offer</span>
          <strong>{activePromotion ? `${activePromotion.discountPercent}%` : loading ? 'Loading...' : 'None'}</strong>
          <small>{activePromotion?.title || 'No active customer promotion loaded.'}</small>
        </article>
        <article className="admin-stat-card">
          <span>Redemptions</span>
          <strong>{activePromotion?.maxRedemptions ?? '-'}</strong>
          <small>Per assigned customer</small>
        </article>
        <article className="admin-stat-card">
          <span>Local limit</span>
          <strong>{activePromotion ? `${activePromotion.localDistanceKm} km` : '-'}</strong>
          <small>Eligible trip distance</small>
        </article>
      </section>

      <section className="admin-panel">
        <div className="admin-panel-heading">
          <div>
            <h2>Promotion settings</h2>
            <p className="admin-panel-copy">Changes apply to newly assigned signup offers. Existing user assignments keep their saved percentage.</p>
          </div>
          <div className="admin-promotion-panel-actions">
            <button className="register-secondary-button" disabled={loading || saving} onClick={() => void loadPromotions()} type="button">
              Refresh
            </button>
            <button
              aria-expanded={settingsExpanded}
              aria-controls="customer-promotion-settings-form"
              className="admin-promotion-edit-button"
              onClick={() => setSettingsExpanded((current) => !current)}
              type="button"
            >
              {settingsExpanded ? (
                <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
                  <path d="m6 15 6-6 6 6" />
                </svg>
              ) : (
                <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                </svg>
              )}
              <span>{settingsExpanded ? 'Hide form' : 'Edit active promotion'}</span>
            </button>
          </div>
        </div>

        <div className="admin-promotion-summary-row">
          <div>
            <span>Code</span>
            <strong>{form.code || 'Auto-generated'}</strong>
          </div>
          <div>
            <span>Status</span>
            <strong>{normalizeLabel(form.status)}</strong>
          </div>
          <div>
            <span>Offer</span>
            <strong>{numberValue(form.discountPercent, 0)}% x {numberValue(form.maxRedemptions, 0)}</strong>
          </div>
          <div>
            <span>Limit</span>
            <strong>{numberValue(form.localDistanceKm, 0)} km</strong>
          </div>
          <div>
            <span>Signup</span>
            <strong>{form.assignOnSignup ? 'Assigned' : 'Off'}</strong>
          </div>
        </div>

        {settingsExpanded ? (
          <form className="admin-campaign-form admin-promotion-settings-form" id="customer-promotion-settings-form" onSubmit={submitPromotion}>
            <label className="register-label">
              Code
              <input className="register-input" onChange={(event) => updateForm('code', event.target.value)} placeholder="FIRST_LOCAL_SHIFT_50" value={form.code} />
            </label>
            <label className="register-label">
              Status
              <select className="register-input" onChange={(event) => updateForm('status', event.target.value)} value={form.status}>
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
                <option value="DRAFT">Draft</option>
                <option value="ENDED">Ended</option>
              </select>
            </label>
            <label className="register-label">
              Discount percent
              <input className="register-input" max="100" min="1" onChange={(event) => updateForm('discountPercent', event.target.value)} required type="number" value={form.discountPercent} />
            </label>
            <label className="register-label">
              Max redemptions
              <input className="register-input" min="1" onChange={(event) => updateForm('maxRedemptions', event.target.value)} required type="number" value={form.maxRedemptions} />
            </label>
            <label className="register-label">
              Local distance km
              <input className="register-input" min="1" onChange={(event) => updateForm('localDistanceKm', event.target.value)} required step="0.1" type="number" value={form.localDistanceKm} />
            </label>
            <label className="register-label">
              Starts at
              <input className="register-input" onChange={(event) => updateForm('startsAt', event.target.value)} required type="datetime-local" value={form.startsAt} />
            </label>
            <label className="register-label">
              Ends at
              <input className="register-input" onChange={(event) => updateForm('endsAt', event.target.value)} type="datetime-local" value={form.endsAt} />
            </label>
            <label className="register-label">
              Title
              <input className="register-input" onChange={(event) => updateForm('title', event.target.value)} placeholder={previewTitle} value={form.title} />
            </label>
            <label className="register-label admin-promotion-form-wide">
              Description
              <textarea className="register-input" onChange={(event) => updateForm('description', event.target.value)} rows={3} value={form.description} />
            </label>
            <label className="admin-checkbox-row admin-campaign-checkbox">
              <input checked={form.assignOnSignup} onChange={(event) => updateForm('assignOnSignup', event.target.checked)} type="checkbox" />
              Assign this promotion when new customers register
            </label>

            <div className="admin-form-actions admin-promotion-form-wide">
              <button className="register-submit" disabled={saving || loading} type="submit">
                {saving ? 'Saving...' : 'Save signup promotion'}
              </button>
            </div>
          </form>
        ) : null}
      </section>

      <section className="admin-panel">
        <div className="admin-panel-heading">
          <div>
            <h2>Recent promotion records</h2>
            <p className="admin-panel-copy">The active signup offer is the one assigned to new accounts.</p>
          </div>
        </div>

        <div className="admin-table">
          <div className="admin-table-head admin-table-head-promotions">
            <span>Offer</span>
            <span>Status</span>
            <span>Window</span>
            <span>Discount</span>
            <span>Scope</span>
            <span>Updated</span>
          </div>
          {history.map((promotion) => (
            <div className="admin-table-row admin-table-row-promotions" key={promotion.campaignId || promotion.code}>
              <div>
                <strong>{promotion.title}</strong>
                <small>{promotion.code}</small>
              </div>
              <span className={statusPillClass(promotion.status)}>{normalizeLabel(promotion.status)}</span>
              <div>
                <strong>{formatDateTime(promotion.startsAt)}</strong>
                <small>{promotion.endsAt ? formatDateTime(promotion.endsAt) : 'No end date'}</small>
              </div>
              <div>
                <strong>{promotion.discountPercent}%</strong>
                <small>{promotion.maxRedemptions} redemption{promotion.maxRedemptions === 1 ? '' : 's'}</small>
              </div>
              <div>
                <strong>{promotion.localDistanceKm} km</strong>
                <small>{promotion.assignOnSignup ? 'Signup assignment' : 'Manual only'}</small>
              </div>
              <div>
                <strong>{formatDateTime(promotion.updatedAt || promotion.createdAt)}</strong>
                <small>{promotion.updatedByAdminEmail || promotion.createdByAdminEmail || 'Admin'}</small>
              </div>
            </div>
          ))}
          {!loading && history.length === 0 ? <p className="admin-empty">No promotion records yet.</p> : null}
          {loading ? <p className="admin-empty">Loading customer promotions...</p> : null}
        </div>
      </section>
    </AdminShell>
  );
};

export default AdminCustomerPromotionsPage;
